import React, { useState } from 'react';
import { updateProfile } from '../api';

export default function OnboardingModal({ user, onComplete }) {
  const [step, setStep] = useState(1);
  const [nickname, setNickname] = useState('');
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const hkId = user?.hkId || 'you';

  const saveAndAdvance = async () => {
    if (!nickname.trim()) { setStep(3); return; }
    setSaving(true);
    try { await updateProfile({ displayName: nickname.trim() }); } catch (_) {}
    setSaving(false);
    setStep(3);
  };

  const finish = (startTour) => {
    setClosing(true);
    setTimeout(() => onComplete(nickname.trim(), startTour), 380);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      background: closing ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0.75)',
      backdropFilter: closing ? 'blur(0px)' : 'blur(14px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
      transition: 'background 0.38s ease, backdrop-filter 0.38s ease',
      pointerEvents: closing ? 'none' : 'auto',
    }}>
      <div style={{
        background: 'var(--surface)', borderRadius: 28, width: '100%', maxWidth: 480,
        boxShadow: '0 40px 80px rgba(0,0,0,0.35)',
        overflow: 'hidden', position: 'relative',
        animation: closing
          ? 'onboardFadeDown 0.38s cubic-bezier(0.4,0,1,1) forwards'
          : 'onboardFadeUp 0.4s cubic-bezier(0.16,1,0.3,1)',
      }}>

        {/* Progress dots */}
        <div style={{ display: 'flex', gap: 6, position: 'absolute', top: 22, left: '50%', transform: 'translateX(-50%)' }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{
              width: s === step ? 20 : 7, height: 7, borderRadius: 4,
              background: s <= step ? '#0ea5e9' : '#e2e8f0',
              transition: 'all 0.35s cubic-bezier(0.16,1,0.3,1)',
            }} />
          ))}
        </div>

        {/* ── STEP 1: Welcome ── */}
        {step === 1 && (
          <div style={{ padding: '56px 24px 32px', textAlign: 'center' }}>
            <div style={{ marginBottom: 20 }}>
              <img src="/logo-mascot.png" alt="mascot" style={{
                width: 110, height: 110, objectFit: 'contain',
                filter: 'drop-shadow(0 8px 24px rgba(14,165,233,0.25))',
              }} />
            </div>

            <div style={{
              display: 'inline-block', background: 'linear-gradient(135deg,#e0f2fe,#bae6fd)',
              color: '#0284c7', borderRadius: 100, padding: '4px 14px',
              fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
              marginBottom: 16,
            }}>New Member</div>

            <h1 style={{
              fontFamily: 'var(--display)', fontSize: 30, fontWeight: 900,
              color: 'var(--text)', margin: '0 0 12px', letterSpacing: -0.8, lineHeight: 1.2,
            }}>
              Welcome to<br /><span style={{ color: '#0ea5e9' }}>HireKahani</span>
            </h1>

            <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.75, margin: '0 0 28px' }}>
              India's home for honest interview experiences.<br />
              Share your story — it helps thousands of job seekers make smarter career decisions.
            </p>

            <div style={{
              background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
              border: '1px solid #bbf7d0', borderRadius: 16,
              padding: '16px 20px', marginBottom: 32, textAlign: 'left',
              display: 'flex', gap: 14, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 26, flexShrink: 0 }}>🔒</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 13, color: '#15803d', marginBottom: 4 }}>
                  You control your identity
                </div>
                <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.65 }}>
                  Your real name is never shown. You can choose a <strong>display name</strong> that appears on your Kahanis, or use your unique ID (<strong>{hkId}</strong>) — the choice is yours.
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              style={{
                width: '100%', background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                color: '#fff', border: 'none', borderRadius: 14,
                padding: '15px', fontSize: 15, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'var(--sans)',
                boxShadow: '0 8px 24px rgba(14,165,233,0.35)', transition: 'all 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Next →
            </button>
          </div>
        )}

        {/* ── STEP 2: Display Name ── */}
        {step === 2 && (
          <div style={{ padding: '56px 24px 32px', textAlign: 'center', animation: 'stepSlideIn 0.3s ease-out' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>✍️</div>

            <h2 style={{
              fontFamily: 'var(--display)', fontSize: 26, fontWeight: 900,
              color: 'var(--text)', margin: '0 0 10px', letterSpacing: -0.6,
            }}>
              Choose your display name
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text2)', margin: '0 0 20px', lineHeight: 1.7 }}>
              This name will appear on every Kahani you share with the community.
            </p>

            {/* Preview chip */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#f1f5f9', borderRadius: 100, padding: '6px 14px',
              fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 24,
            }}>
              <span style={{ color: '#94a3b8', fontWeight: 500 }}>Shows as:</span>
              <span style={{ color: '#0ea5e9' }}>{nickname.trim() || hkId}</span>
            </div>

            <div style={{ textAlign: 'left', marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>
                Display Name
              </label>
              <input
                value={nickname}
                onChange={e => setNickname(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && nickname.trim()) saveAndAdvance(); }}
                placeholder="e.g. Rahul, Dev, The Elephant 🐘"
                maxLength={30}
                style={{
                  width: '100%', padding: '14px 18px', borderRadius: 12,
                  border: '2px solid var(--border)', background: 'var(--surface2)',
                  fontSize: 16, color: 'var(--text)', outline: 'none',
                  fontFamily: 'var(--sans)', boxSizing: 'border-box', transition: 'border-color 0.2s',
                }}
                onFocus={e => e.target.style.borderColor = '#0ea5e9'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            {nickname.trim() && (
              <div style={{
                background: 'linear-gradient(135deg,#fff7ed,#ffedd5)',
                border: '1px solid #fed7aa', borderRadius: 12,
                padding: '12px 16px', marginBottom: 16,
                display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left',
              }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>👁️</span>
                <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.6 }}>
                  <strong>Your display name will be publicly visible</strong> on all Kahanis you post.
                  Anyone on HireKahani can see it. If you prefer privacy, skip and use your ID instead.
                </div>
              </div>
            )}

            <button
              onClick={saveAndAdvance}
              disabled={saving || !nickname.trim()}
              style={{
                width: '100%',
                background: nickname.trim() ? 'linear-gradient(135deg,#0ea5e9,#0284c7)' : '#e2e8f0',
                color: nickname.trim() ? '#fff' : '#94a3b8',
                border: 'none', borderRadius: 14, padding: '15px',
                fontSize: 15, fontWeight: 800,
                cursor: nickname.trim() ? 'pointer' : 'default',
                fontFamily: 'var(--sans)', transition: 'all 0.2s',
                boxShadow: nickname.trim() ? '0 8px 24px rgba(14,165,233,0.3)' : 'none',
              }}
            >
              {saving ? 'Saving…' : nickname.trim() ? 'Set Display Name →' : 'Enter a name to continue'}
            </button>

            <button
              onClick={() => setStep(3)}
              style={{
                background: 'none', border: 'none', marginTop: 14,
                fontSize: 13, color: '#94a3b8', cursor: 'pointer',
                fontFamily: 'var(--sans)',
              }}
            >
              Skip — use my ID (<strong style={{ color: '#0ea5e9' }}>{hkId}</strong>) instead
            </button>
          </div>
        )}

        {/* ── STEP 3: Tour offer ── */}
        {step === 3 && (
          <div style={{ padding: '56px 24px 32px', textAlign: 'center', animation: 'stepSlideIn 0.3s ease-out' }}>
            <div style={{ fontSize: 60, marginBottom: 18 }}>🗺️</div>

            <h2 style={{
              fontFamily: 'var(--display)', fontSize: 26, fontWeight: 900,
              color: 'var(--text)', margin: '0 0 10px', letterSpacing: -0.6,
            }}>
              {nickname.trim() ? `Ready, ${nickname.trim()}?` : 'One last thing…'}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--text2)', margin: '0 0 28px', lineHeight: 1.75 }}>
              Would you like a quick <strong style={{ color: '#0f172a' }}>2-minute tour</strong> of the dashboard?<br />
              We'll show you where everything is so you can hit the ground running.
            </p>

            {/* Feature preview chips */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 32 }}>
              {['📊 Stats Bar', '🗂 Navigation', '✍️ Share Kahani', '📰 The Feed', '⚡ Live Activity'].map(f => (
                <span key={f} style={{
                  background: '#f1f5f9', borderRadius: 100, padding: '5px 12px',
                  fontSize: 12, fontWeight: 600, color: '#475569',
                }}>
                  {f}
                </span>
              ))}
            </div>

            {/* Buttons */}
            <button
              onClick={() => finish(true)}
              style={{
                width: '100%', background: 'linear-gradient(135deg,#0ea5e9,#0284c7)',
                color: '#fff', border: 'none', borderRadius: 14,
                padding: '15px', fontSize: 15, fontWeight: 800,
                cursor: 'pointer', fontFamily: 'var(--sans)',
                boxShadow: '0 8px 24px rgba(14,165,233,0.35)',
                transition: 'all 0.2s', marginBottom: 10,
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              🚀 Start Tour
            </button>
            <button
              onClick={() => finish(false)}
              style={{
                width: '100%', background: 'transparent',
                color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 14,
                padding: '14px', fontSize: 14, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--sans)', transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#475569'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; }}
            >
              Skip Tutorial
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes onboardFadeUp {
          from { opacity: 0; transform: translateY(32px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes onboardFadeDown {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to   { opacity: 0; transform: translateY(20px) scale(0.96); }
        }
        @keyframes stepSlideIn {
          from { opacity: 0; transform: translateX(18px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
